import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Incremented on any manual auth action (loginWithToken / setUser / logout).
  // A background /auth/me probe captures the epoch before firing and must not
  // clobber state if the epoch has moved on.
  const authEpoch = useRef(0);

  const checkAuth = useCallback(async () => {
    const epochAtStart = authEpoch.current;
    try {
      const { data } = await api.get("/auth/me");
      if (authEpoch.current === epochAtStart) {
        setUser(data);
      }
    } catch (err) {
      // Only treat a genuine 401 as "not logged in".
      // Network errors, 5xx, and CORS blips should NOT wipe the current user.
      const status = err?.response?.status;
      const genuinelyUnauth = status === 401 || status === 403;
      if (genuinelyUnauth && authEpoch.current === epochAtStart) {
        setUser(null);
      }
    } finally {
      if (authEpoch.current === epochAtStart) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Skip /me during OAuth callback — AuthCallback establishes session first
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Skip the probe entirely if we have no token stored — nothing to restore.
    // This eliminates the stale-401 race for email/password users and removes
    // the console 401 noise on public pages.
    const hasToken =
      typeof window !== "undefined" && !!window.localStorage?.getItem("ps_token");
    if (!hasToken) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithToken = (data) => {
    authEpoch.current += 1;
    localStorage.setItem("ps_token", data.token);
    setUser(data.user);
    setLoading(false);
  };

  const setUserDirect = (u) => {
    authEpoch.current += 1;
    setUser(u);
    setLoading(false);
  };

  const logout = async () => {
    authEpoch.current += 1;
    setUser(null);
    localStorage.removeItem("ps_token");
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, setUser: setUserDirect, loading, checkAuth, loginWithToken, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
